/**
 * PayerCopayTable — the type toggle + per-billing-group breakdown shared
 * by the insurance (co-pay) and corporate (discount) payer editors.
 *
 * Flutter parity: `edit_health_insurance_widget.dart:560+` (co-pay) and
 * `edit_corporate_agreement_widget.dart` (discount). The two differ only
 * in:
 *   - header label: "Co-Pay" (insurance) vs "Discount" (corporate)
 *   - the Max-Copay column: insurance shows it; corporate does NOT
 *     (corporate `billingGroups` ship `{billingGroupId, category, value}`
 *     only — confirmed against the 2026-06-05 /api/Patients/PatientCorporate
 *     dev curl).
 *
 * Wire type values — Flutter `app_constants.dart:649-650`:
 *   - `"fixed"`  → first pill (Fixed, {currency})
 *   - `"pct"`    → second pill (Pct, %)
 *
 * Generic over the billing-group item `T` so both the scheduling
 * `PayerBillingGroup` and the patient-module `PatientPayerBillingGroup`
 * flow through without a cast. Both carry nullable `value` /
 * `maximumCopayment`: a blanked field clears to null rather than being
 * coerced to 0, and `as Partial<T>` below would hide a non-null declaration
 * rather than reject it.
 *
 * The `makeGroup` factory lets each caller build a freshly-selected row
 * in its own shape (corporate omits `maximumCopayment`).
 */

import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { BillingGroup } from '@/shared/types/billing';

/** Wire values — Flutter `app_constants.dart:649-650`. */
export const COPAY_TYPE_FIXED = 'fixed';
export const COPAY_TYPE_PCT = 'pct';

/** Structural minimum every payer billing-group item satisfies. */
export interface PayerCopayGroupLike {
  billingGroupId?: string | null;
  category?: string | null;
  value?: number | null;
  maximumCopayment?: number | null;
}

interface CopaymentTypeSelectorProps {
  value: string | null | undefined;
  currencyCode: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}

/** Two-pill segmented selector. First pill = Fixed (`"fixed"`),
 *  second = Percentage (`"pct"`). */
function CopaymentTypeSelector({
  value,
  currencyCode,
  onChange,
  disabled,
}: CopaymentTypeSelectorProps) {
  const isFixed = value === COPAY_TYPE_FIXED;
  const isPct = value === COPAY_TYPE_PCT;
  const pill = (active: boolean) =>
    cn(
      'rounded-full px-3 py-1 text-sm font-semibold transition-colors',
      active
        ? 'bg-brand-primary text-white'
        : 'bg-grey-100 text-brand-primary hover:bg-grey-200',
      disabled && 'opacity-50 cursor-not-allowed',
    );
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(COPAY_TYPE_FIXED)}
        className={pill(isFixed)}
      >
        Fixed, {currencyCode}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(COPAY_TYPE_PCT)}
        className={pill(isPct)}
      >
        Pct, %
      </button>
    </div>
  );
}

export interface PayerCopayTableProps<T extends PayerCopayGroupLike> {
  billingGroups: BillingGroup[];
  selected: T[];
  onChange: (next: T[]) => void;
  copayType: string | null | undefined;
  onCopayTypeChange: (next: string) => void;
  currencyCode: string;
  /** Header label — "Co-Pay" (insurance) | "Discount" (corporate). */
  label?: string;
  /** Insurance shows a Max-Copay column; corporate does not. */
  showMaxCopay?: boolean;
  /** Build a freshly-selected group row in the caller's item shape. */
  makeGroup: (group: BillingGroup) => T;
  disabled?: boolean;
}

export function PayerCopayTable<T extends PayerCopayGroupLike>({
  billingGroups,
  selected,
  onChange,
  copayType,
  onCopayTypeChange,
  currencyCode,
  label = 'Co-Pay',
  showMaxCopay = true,
  makeGroup,
  disabled,
}: PayerCopayTableProps<T>) {
  const selectedByGroupId = useMemo(() => {
    const m = new Map<string, T>();
    for (const item of selected) {
      if (item.billingGroupId) m.set(item.billingGroupId, item);
    }
    return m;
  }, [selected]);

  const allSelected =
    billingGroups.length > 0 &&
    billingGroups.every((g) => g.id && selectedByGroupId.has(g.id));

  const toggleAll = () => {
    if (disabled) return;
    if (allSelected) {
      onChange([]);
    } else {
      onChange(
        billingGroups
          .filter((g) => !!g.id)
          .map((g) => {
            const existing = selectedByGroupId.get(g.id!);
            // Preserve any already-entered value when re-selecting all.
            return existing ?? makeGroup(g);
          }),
      );
    }
  };

  const toggleOne = (group: BillingGroup) => {
    if (disabled || !group.id) return;
    if (selectedByGroupId.has(group.id)) {
      onChange(selected.filter((s) => s.billingGroupId !== group.id));
    } else {
      onChange([...selected, makeGroup(group)]);
    }
  };

  const updateRow = (groupId: string, patch: Partial<T>) => {
    onChange(
      selected.map((s) =>
        s.billingGroupId === groupId ? { ...s, ...patch } : s,
      ),
    );
  };

  const gridCols = showMaxCopay
    ? 'grid-cols-[1fr_88px_88px]'
    : 'grid-cols-[1fr_88px]';
  const numberCls =
    'h-8 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0';

  return (
    <div>
      {/* Header row: label + type pills (inline) + Select All (right) */}
      <div className="flex items-center gap-2 mb-2">
        <Label className="text-xs font-medium text-text-muted">
          {label}
        </Label>
        <CopaymentTypeSelector
          value={copayType}
          currencyCode={currencyCode}
          onChange={onCopayTypeChange}
          disabled={disabled}
        />
        <div className="flex-1" />
        <button
          type="button"
          onClick={toggleAll}
          disabled={disabled}
          className="text-sm font-semibold text-brand-primary hover:text-interactive-hover disabled:opacity-50"
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </button>
      </div>
      <div
        className={cn(
          'grid gap-2 items-center text-xs font-medium text-text-muted mb-1',
          gridCols,
        )}
      >
        <span>{label}</span>
        <span>Value</span>
        {showMaxCopay ? <span>Max Copay</span> : null}
      </div>
      <div className="space-y-2">
        {billingGroups.map((group) => {
          if (!group.id) return null;
          const row = selectedByGroupId.get(group.id);
          const checked = !!row;
          return (
            <div
              key={group.id}
              className={cn('grid gap-2 items-center', gridCols)}
            >
              <button
                type="button"
                onClick={() => toggleOne(group)}
                disabled={disabled}
                className="flex items-center gap-2 text-left"
              >
                <span
                  className={cn(
                    'h-4 w-4 rounded-full border-2 inline-flex items-center justify-center flex-shrink-0',
                    checked ? 'border-brand-primary' : 'border-border-default',
                  )}
                >
                  {checked ? (
                    <span className="h-2 w-2 rounded-full bg-brand-primary" />
                  ) : null}
                </span>
                <span className="text-sm text-text-primary truncate">
                  {group.category ?? '—'}
                </span>
              </button>
              <Input
                type="number"
                inputMode="decimal"
                value={row?.value ?? ''}
                disabled={!checked || disabled}
                placeholder="0.00"
                onChange={(e) => {
                  // Mirrors Flutter edit_health_insurance_widget.dart:929-943 --
                  // valid when parseable AND > -1 AND, for percentage co-pay,
                  // <= 100. Anything else (including empty) clears to null.
                  // 0 is deliberately VALID here: it means no patient share.
                  const raw = e.target.value;
                  const n = Number(raw);
                  const isPct = copayType !== COPAY_TYPE_FIXED;
                  const ok =
                    raw !== '' &&
                    Number.isFinite(n) &&
                    n > -1 &&
                    (isPct ? n <= 100 : true);
                  updateRow(group.id!, {
                    value: ok ? n : null,
                  } as Partial<T>);
                }}
                className={numberCls}
              />
              {showMaxCopay ? (
                <Input
                  type="number"
                  inputMode="decimal"
                  value={row?.maximumCopayment ?? ''}
                  disabled={!checked || disabled}
                  placeholder="Max"
                  onChange={(e) => {
                    // Mirrors Flutter edit_health_insurance_widget.dart:979-989 --
                    // valid only when > 0. Unlike the co-pay value, 0 is NOT
                    // meaningful for a maximum, so it clears to null.
                    const raw = e.target.value;
                    const n = Number(raw);
                    const ok = raw !== '' && Number.isFinite(n) && n > 0;
                    updateRow(group.id!, {
                      maximumCopayment: ok ? n : null,
                    } as Partial<T>);
                  }}
                  className={numberCls}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
